// Peringatan: the page, its filters, the rows, the drawer, the close dialog, the header bell and the toasts.
// Formal and calm: no exclamation marks; "Titik Distribusi" and "Reader" in every sentence (API ids stay English).
import type { AlertState } from '@lib/api/alerts';
import type { AlertType, Severity } from '@lib/api/common';

/** Tingkat. Never "Peringatan": that is the page's own name. */
export const SEVERITY_TEXT: Record<Severity, string> = { critical: 'Kritis', warning: 'Penting', info: 'Informasi' };

/** Jenis: short, formal labels (filters, rows, the drawer, the activity log). */
export const ALERT_TYPE_TEXT: Record<AlertType, string> = {
  GATEWAY_OFFLINE: 'Reader terputus',
  READER_ERROR: 'Gangguan pembacaan tag',
  CLOCK_UNSYNCED: 'Jam Reader belum sinkron',
  TEST_DUE: 'Lewat jadwal uji berkala',
  CLONE_SUSPECT: 'Indikasi tag ganda',
  TAG_REWRITTEN: 'Tag ditulis ulang',
  SEEN_WITHOUT_SHIPMENT: 'Tabung terbaca tanpa Surat Jalan',
  SHIPMENT_EXTRA_TAG: 'Tabung di luar muatan',
  SHIPMENT_MISSING: 'Tabung tidak diterima',
  SHIPMENT_OFF_NETWORK: 'Kiriman di luar jaringan',
  SEQ_GAP_UNRECOVERABLE: 'Data bacaan tidak terpulihkan',
  BAD_PAYLOAD: 'Pesan tidak valid',
  STOCK_GAP: 'Stok tabung isi tidak tersedia untuk desa/kelurahan',
  ORDER_SLA_BREACHED: 'Pengantaran melewati batas waktu',
  ORDER_OVERCHARGED: 'Pembayaran melebihi tagihan',
  ORDER_CONFIRMATION_LOCKED: 'Kode konfirmasi terkunci',
  EXCHANGE_FOREIGN_EMPTY: 'Tabung kosong milik rumah tangga lain',
  EXCHANGE_MISMATCH: 'Penukaran tidak sesuai bacaan Reader',
  HOUSEHOLD_HOARDING: 'Tabung yang dipegang melebihi batas',
  LOST_CYLINDER_SEEN: 'Tabung hilang terbaca kembali',
  HANDOVER_NOT_LEFT_HUB: 'Tabung tercatat di warga tetapi masih terbaca di Hub',
  TAG_SUSPECTED_DEAD: 'Dugaan tag rusak',
  COURIER_MISMATCH_PATTERN: 'Ketidaksesuaian berulang oleh kurir',
  EMPTY_NOT_RETURNED: 'Tabung kosong belum kembali ke Hub',
  ORDER_CONFIRMED_WITHOUT_CODE: 'Penukaran dikonfirmasi tanpa kode',
  DELIVERY_FAR_FROM_HOME: 'Penukaran jauh dari titik rumah',
  COURIER_MULTI_DEVICE: 'Akun kurir dipakai di perangkat lain',
  RETEST_OVERDUE_AT_HOUSEHOLD: 'Tabung di rumah tangga lewat uji berkala',
};

export const ALERTS = {
  title: 'Peringatan',
  sub: 'Gangguan dan kejadian yang perlu diperiksa.',
  loading: 'Memuat peringatan…',
  noun: 'peringatan',
  results: 'Daftar peringatan',
} as const;

/** The three states, as the tabs say them (laptops) and as they fit a phone. */
export const ALERT_TABS: { label: string } & Record<AlertState, string> = {
  label: 'Status peringatan',
  needs_action: 'Perlu tindakan',
  handling: 'Sedang ditindaklanjuti',
  resolved: 'Selesai',
};
export const ALERT_TABS_SHORT: Record<AlertState, string> = { needs_action: 'Perlu tindakan', handling: 'Ditindaklanjuti', resolved: 'Selesai' };

export const ALERT_FILTERS = {
  search: 'Cari peringatan',
  searchPlaceholder: 'Cari nomor seri, reader, atau titik distribusi…',
  searchPlaceholderShort: 'Cari peringatan…',
  severity: 'Tingkat',
  type: 'Jenis',
  typesChosen: (n: number) => `${n} jenis`,
  severitiesChosen: (n: number) => `${n} tingkat`,
  region: 'Wilayah',
  when: 'Waktu',
  /** the chip of a list narrowed to one reader (from the reader page) */
  reader: 'Reader',
  /** the list narrowed to one cylinder (from its page) */
  cylinder: 'Tabung',
  someCylinder: 'Tabung terpilih',
  /** the list narrowed to one Surat Jalan (from its page) */
  shipment: 'Surat Jalan',
  someShipment: 'Surat Jalan terpilih',
} as const;

export const ALERT_EMPTY = {
  needs_action: 'Tidak ada peringatan yang perlu ditindaklanjuti.',
  needsActionHandling: (n: string) => `${n} peringatan sedang ditindaklanjuti.`,
  showHandling: 'Lihat yang sedang ditindaklanjuti',
  handling: 'Tidak ada peringatan yang sedang ditindaklanjuti.',
  resolved: 'Belum ada peringatan yang selesai.',
  noMatch: { title: 'Tidak ada peringatan yang cocok', text: 'Ubah kata kunci, tingkat, jenis, wilayah, atau waktu.' },
} as const;

/** One row: its meta line, the handler line, the actions. */
export const ALERT_ROW = {
  handledBy: (name: string, when: string) => `Ditindaklanjuti ${name} · ${when}`,
  handledBySomeone: (when: string) => `Ditindaklanjuti · ${when}`,
  resolvedAuto: (when: string) => `Selesai otomatis · ${when}`,
  resolvedBy: (name: string, when: string) => `Ditutup ${name} · ${when}`,
  resolvedAt: (when: string) => `Selesai · ${when}`,
  reader: (label: string) => `Reader ${label}`,
  select: (title: string) => `Pilih: ${title}`,
  selectPage: 'Pilih semua di halaman ini',
  open: (title: string) => `Rincian: ${title}`,
  menu: (title: string) => `Tindakan untuk ${title}`,
} as const;

export const ALERT_ACTIONS = {
  ack: 'Tindak lanjuti',
  view: 'Lihat rincian',
  close: 'Tutup',
  /** in the drawer, where a bare "Tutup" would read as closing the panel */
  closeAlert: 'Tutup peringatan',
  bulkAck: (n: number) => `Tindak lanjuti (${n})`,
  selected: (n: string) => `${n} dipilih`,
  selectionLabel: 'Peringatan terpilih',
  clearSelection: 'Batalkan pilihan',
  acked: 'Peringatan ditindaklanjuti',
  ackedMany: (n: string) => `${n} peringatan ditindaklanjuti`,
  ackedSome: (done: string, asked: string) => `${done} dari ${asked} peringatan ditindaklanjuti. Sisanya sudah ditindaklanjuti atau selesai.`,
  closed: 'Peringatan ditutup',
  /** why the action is not offered, as its tooltip (the backend refuses the same with OUT_OF_SCOPE) */
  blocked: {
    national: 'Peringatan sistem tanpa titik distribusi hanya dapat ditindaklanjuti admin nasional.',
    elsewhere: 'Peringatan ini terjadi di titik distribusi lain. Petugas menindaklanjuti peringatan di titik distribusi tugasnya.',
  },
} as const;

/** Tutup: always with a note; a type that clears by itself only by an admin, with a word of caution. */
export const ALERT_CLOSE = {
  title: 'Tutup peringatan',
  lead: (title: string) => `Peringatan “${title}” akan dipindahkan ke Selesai.`,
  autoNote: 'Peringatan jenis ini biasanya selesai otomatis saat kondisinya pulih. Tutup secara manual hanya bila kondisinya sudah ditangani dengan cara lain.',
  note: 'Catatan penutupan',
  noteHelp: 'Jelaskan apa yang sudah dilakukan. Catatan ini tersimpan di riwayat peringatan.',
  noteRequired: 'Tulis catatan penutupan.',
  submit: 'Tutup peringatan',
} as const;

export const ALERT_DRAWER = {
  label: (title: string) => `Rincian peringatan: ${title}`,
  what: 'Keterangan',
  related: 'Terkait',
  history: 'Riwayat',
  note: 'Catatan (opsional)',
  noteHelp: 'Tersimpan di riwayat bersama tindak lanjut Anda.',
  facts: { severity: 'Tingkat', type: 'Jenis', state: 'Status', raised: 'Muncul', id: 'Nomor' },
  objects: { location: 'Titik distribusi', reader: 'Reader', cylinder: 'Tabung', shipment: 'Surat Jalan' },
  noObjects: 'Peringatan sistem, tidak terkait titik distribusi.',
  timeline: {
    raised: 'Peringatan muncul',
    acked: (name: string | null) => (name ? `Ditindaklanjuti ${name}` : 'Ditindaklanjuti'),
    resolvedAuto: 'Selesai otomatis',
    resolved: (name: string | null) => (name ? `Ditutup ${name}` : 'Ditutup'),
    reopened: 'Muncul kembali',
  },
  historyFailed: 'Riwayat belum dapat dimuat.',
  missing: 'Peringatan ini tidak ditemukan atau berada di luar wilayah akses Anda.',
  autoHint: 'Selesai otomatis saat kondisinya pulih.',
} as const;

export const ALERT_BELL = {
  name: (n: number) => (n > 0 ? `Peringatan, ${n} perlu tindakan` : 'Peringatan'),
  head: 'Perlu tindakan',
  seeAll: (n: string) => `Lihat semua (${n})`,
  seeList: 'Buka Peringatan',
  calm: 'Tidak ada peringatan yang perlu ditindaklanjuti.',
  calmHandling: (n: string) => `Tidak ada yang perlu ditindaklanjuti · ${n} sedang ditindaklanjuti`,
  failed: 'Peringatan belum dapat dimuat.',
  stale: 'Gagal memperbarui.',
} as const;

export const ALERT_TOAST = {
  critical: (title: string) => `Peringatan kritis: ${title}`,
  criticalMany: (n: string) => `${n} peringatan kritis baru`,
  view: 'Lihat',
} as const;
